import { useCallback, useEffect, useState } from 'react';
import { initAnalytics } from '@/lib/analytics';
import {
  COOKIE_CONSENT_EVENT_NAME,
  COOKIE_CONSENT_KEY,
  type CookieConsentState,
  getStoredCookieConsent,
  setStoredCookieConsent,
} from '@/lib/cookie-consent';

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsentState>(() => getStoredCookieConsent());

  useEffect(() => {
    function handleUpdate(event: Event) {
      const detail = (event as CustomEvent<CookieConsentState>).detail ?? null;
      setConsent(detail);
      if (detail === 'all') {
        initAnalytics();
      }
    }

    function handleStorage(event: StorageEvent) {
      if (event.key === COOKIE_CONSENT_KEY) {
        const next = getStoredCookieConsent();
        setConsent(next);
        if (next === 'all') {
          initAnalytics();
        }
      }
    }

    window.addEventListener(COOKIE_CONSENT_EVENT_NAME, handleUpdate as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT_NAME, handleUpdate as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    if (consent === 'all') {
      initAnalytics();
    }
  }, [consent]);

  const accept = useCallback((value: 'all' | 'essential') => {
    setStoredCookieConsent(value);
    setConsent(value);
    if (value === 'all') {
      initAnalytics();
    }
  }, []);

  return { consent, accept };
}
